export type ValueFormat = 'decimal' | 'percent' | 'integer' | 'currency';

export const sanitizeNumber = (val = '') => String(val).replace(/\D/g, '');

export const formatNumberToPTBRCurrency = (amount: number) => {
  if (Number.isNaN(Number(amount))) {
    return '--';
  }

  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Math.abs(amount));

  return amount < 0 ? `R$ -${formatted.substring(3)}` : formatted;
};

export const formatNumberToPTBRStyle = ({
  amount,
  decimalPlaces = 2,
}: {
  amount: number;
  decimalPlaces?: number;
}) =>
  Number(amount).toLocaleString('pt-br', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

export const formatToPercentage = (
  value: number | string,
  decimalPlaces = 2,
  roundToMinimum = false
): string => {
  if (Number.isNaN(Number(value))) {
    return '--';
  }

  const numericValue = Number(value);
  let formattedValue = numericValue;

  if (roundToMinimum && numericValue !== 0) {
    const rounded = Number(numericValue.toFixed(decimalPlaces));
    if (rounded === 0) {
      const minValue = 1 / Math.pow(10, decimalPlaces);
      formattedValue = numericValue >= 0 ? minValue : -minValue;
    }
  }

  return `${formattedValue
    .toFixed(decimalPlaces)
    .replace('.', ',')
    .replace(`,${'0'.repeat(decimalPlaces)}`, '')}%`;
};

export const formatToMonetary = ({
  value,
  currency = 'BRL',
  locale = 'pt-br',
  notation = 'standard',
  ...rest
}: NumberToMonetaryParams): string =>
  new Intl.NumberFormat(locale, {
    currency,
    notation,
    style: 'currency',
    ...rest,
  }).format(Number(value));

type NumberToMonetaryParams = {
  value: number | string;
  currency?: string;
  locale?: 'pt-br' | 'en-us' | 'fr-fr' | 'ko-kr';
  notation?: 'standard' | 'compact';
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

export const parseToNumber = (value: string, decimals: number = 2): number => {
  const normalizedValue = Number(value.replace(/\D/g, '')) / 100;
  return parseFloat(normalizedValue.toFixed(decimals));
};

export const normalizeString = (word: string) =>
  word.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const formatValue = (
  value: number | null,
  format: ValueFormat,
  maxDecimals?: number
): string => {
  if (value === null) return '-';

  const ptBR = (opts: Intl.NumberFormatOptions) =>
    value.toLocaleString('pt-BR', opts);

  switch (format) {
    case 'percent':
      return (
        ptBR({
          minimumFractionDigits: 1,
          maximumFractionDigits: maxDecimals ?? 2,
        }) + '%'
      );
    case 'decimal':
    case 'integer':
    case 'currency':
      return formatCompactNumber(value);
    default:
      return ptBR({ maximumFractionDigits: maxDecimals ?? 2 });
  }
};

export const formatCompactNumber = (value: number): string => {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const fmt = (n: number): string => {
    const str = n.toFixed(1);
    return str.endsWith('.0') ? String(Math.round(n)) : str.replace('.', ',');
  };
  if (abs >= 1_000_000_000) return `${sign}${fmt(abs / 1_000_000_000)}bi`;
  if (abs >= 1_000_000) return `${sign}${fmt(abs / 1_000_000)}mi`;
  if (abs >= 1_000) return `${sign}${fmt(abs / 1_000)}k`;
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

export const formatMarketPrice = (price: number, currency: string): string => {
  const symbol = currency === 'BRL' ? 'R$' : '$';
  return `${symbol}${price.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatMarketChange = (abs: number, pct: number): string => {
  const sign = abs >= 0 ? '+' : '';
  const absStr = `${sign}${abs.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  const pctStr = Math.abs(pct).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${absStr} (${pctStr}%)`;
};
